<template>
  <div
    class="message-text__wrap"
    :class="{
      'show--quoted': isQuotedContentPresent,
      'hide--quoted': !isQuotedContentPresent,
    }"
  >
    <div v-if="!isEmail" v-dompurify-html="localMessage" class="text-content" />
    <letter v-else class="text-content" :html="localMessage" />
    <div v-if="localPhoneNumber">
      <a-popover
        v-for="(phone, index) in localPhoneNumber"
        :key="index"
        :title="popoverLanguage.title"
        style="margin-right: 5px;"
      >
        <template #content>
          <p>{{ popoverLanguage.content }}</p>
          <div style="text-align: end;">
            <a-button type="primary" @click="confirmUpdatePhoneNumber(phone)">
              {{ popoverLanguage.buttonContent }}
            </a-button>
          </div>
        </template>
        <a href="#" class="phoneNumber">{{ phone }}</a>
      </a-popover>
    </div>
    <button
      v-if="showQuoteToggle"
      class="text-slate-300 dark:text-slate-300 cursor-pointer text-xs py-1"
      @click="toggleQuotedContent"
    >
      <span v-if="showQuotedContent" class="flex items-center gap-0.5">
        <fluent-icon icon="chevron-up" size="16" />
        {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
      </span>
      <span v-else class="flex items-center gap-0.5">
        <fluent-icon icon="chevron-down" size="16" />
        {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
      </span>
    </button>
  </div>
</template>

<script>
import Letter from 'vue-letter';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { parsePhoneNumber } from 'libphonenumber-js';

export default {
  components: { Letter },
  props: {
    message: {
      type: String,
      default: '',
    },
    isEmail: {
      type: Boolean,
      default: true,
    },
    displayQuotedButton: {
      type: Boolean,
      default: false,
    },
    sender: {
      type: Object,
      require: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      showQuotedContent: false,
      // phone number detection
      localMessage: this.message,
      localPhoneNumber: [],
      formatPhoneNumber: '',
      locale: window.chatwootConfig.defaultCountryCode,
      popoverLanguage: {
        title: '',
        content: '',
        buttonContent: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    isQuotedContentPresent() {
        if (!this.isEmail) {
            return this.message.includes('<blockquote');
        }
        return this.showQuotedContent;
    },
    showQuoteToggle() {
        if (!this.isEmail) {
            return false;
        }
        return this.displayQuotedButton;
    },
  },
  // Update format phone number and message (HuuTai)
  watch: {
    message(newMessage) {
      this.localMessage = newMessage;
      this.localPhoneNumber = '';
    },
    locale(newLocale) {
      if (newLocale === 'en') {
        this.popoverLanguage = {
          title: 'Update phone number.',
          content: 'Do you want to update phone number ?',
          buttonContent: 'Update',
        };
      } else if (newLocale === 'vi') {
        this.popoverLanguage = {
          title: 'Cập nhật số điện thoại.',
          content: 'Bạn có muốn cập nhật số điện thoại ?',
          buttonContent: 'Cập nhật',
        };
      }
    },
  },
  // Testing change color phone number(HuuTai)
  async mounted() {
    const regexPhone = /[\d.-]{8,15}/g;
    const regexE164 = /\+\d{1,3}\d{8,15}/g;
    if (this.sender.type === 'contact') {
      if (regexE164.test(this.localMessage)) {
        const phoneNumberWithE164 = this.localMessage.match(regexE164);
        this.localPhoneNumber = phoneNumberWithE164;
        this.localMessage = this.localMessage.replace(regexE164, '');
      } else {
        const phoneNumber = this.localMessage.match(regexPhone);
        this.localMessage = this.localMessage.replace(regexPhone, '');
        this.localPhoneNumber = phoneNumber;
      }
    }
  },
  methods: {
    toggleQuotedContent() {
      this.showQuotedContent = !this.showQuotedContent;
    },
    async confirmUpdatePhoneNumber(phoneNumber) {
      // await this.updatePhoneNumber();
      let phoneNumberPayload = '';
      if (this.locale === 'en') {
        if (phoneNumber.includes('+84')) {
          phoneNumberPayload = phoneNumber;
        } else if (phoneNumber.includes('+1')) {
          phoneNumberPayload = phoneNumber;
        } else {
          const parsePhone = parsePhoneNumber(phoneNumber, 'US');
          phoneNumberPayload = parsePhone.number;
        }
      } else if (this.locale === 'vi') {
        if (phoneNumber.includes('+1')) {
          phoneNumberPayload = phoneNumber;
        } else if (phoneNumber.includes('+84')) {
          phoneNumberPayload = phoneNumber;
        } else {
          const parsePhone = parsePhoneNumber(phoneNumber, 'VN');
          phoneNumberPayload = parsePhone.number;
        }
      }
      await this.updatePhoneNumber(phoneNumberPayload);
    },
    async updatePhoneNumber(phoneNumberPayload) {
      const contactObject = {
        phone_number: phoneNumberPayload,
        id: this.sender.id,
      };
      if (this.sender.type === 'contact') {
        try {
          await this.$store.dispatch('contacts/update', contactObject);
          await this.$store.dispatch(
            'contacts/fetchContactableInbox',
            this.sender.id
          );
          this.showAlert('Update phone number successfully !!');
        } catch (error) {
          if (error.data.includes('phone_number')) {
            this.showAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        }
      } else {
        this.showAlert('This feature is just for customers!!');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~widget/assets/scss/_variables.scss';
.text-content {
  overflow: auto;

  ul,
  ol {
    padding-left: var(--space-two);
  }
  table {
    margin: 0;
    border: 0;

    td {
      margin: 0;
      border: 0;
    }

    tr {
      border-bottom: 0 !important;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: var(--font-size-normal);
  }
}

.show--quoted {
  blockquote {
    @apply block;
  }
}

.hide--quoted {
  blockquote {
    @apply hidden;
  }
}

.message-text__wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  .text-content {
    margin-right: 5px;
  }
  .phoneNumber {
    font-weight: 500;
    text-decoration: underline;
  }
}

.popover-button-yes {
  padding: 0 7px;
  // border: 1px solid ;
}
</style>
